/**
 * Список учебных треков (FR-C7).
 *
 * Каталог отвечает на вопрос «какие задачи есть», трек — «что решать
 * по порядку», поэтому здесь нет ни фильтров, ни группировки: только
 * последовательности в рекомендуемом порядке прохождения.
 */

import { Link } from 'react-router'

import { useTracks, type Track } from '@/api/tracks'
import { strings } from '@/lib/strings'

import { TrackProgressBar } from './TrackProgressBar'

export function TracksPage() {
  const tracks = useTracks()

  return (
    <section className="space-y-6">
      <header className="space-y-1">
        <h1 className="text-2xl font-semibold tracking-tight">{strings.tracks.title}</h1>
        <p className="text-sm text-muted">{strings.tracks.subtitle}</p>
      </header>

      {tracks.isPending && <p className="text-sm text-muted">{strings.tracks.loading}</p>}

      {tracks.isError && (
        <p role="alert" className="text-sm text-warning">
          {strings.tracks.loadFailed}
        </p>
      )}

      {tracks.isSuccess && tracks.data.length === 0 && (
        <div className="rounded-lg border border-dashed border-border bg-surface p-10 text-center">
          <p className="font-medium">{strings.tracks.empty}</p>
        </div>
      )}

      <ul className="space-y-3">
        {(tracks.data ?? []).map((track) => (
          <li key={track.slug}>
            <TrackCard track={track} />
          </li>
        ))}
      </ul>
    </section>
  )
}

function TrackCard({ track }: { track: Track }) {
  const solved = track.tasks_solved

  return (
    <article className="rounded-lg border border-border bg-surface p-4">
      <header className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <h2 className="font-semibold">
          <Link to={`/tracks/${track.slug}`} className="hover:underline">
            {track.title}
          </Link>
        </h2>
        <p className="text-xs text-muted">
          {solved === null
            ? strings.tracks.steps(track.tasks_total)
            : strings.tracks.stepsSolved(solved, track.tasks_total)}
        </p>
      </header>

      {track.description !== null && <p className="mt-2 text-sm text-muted">{track.description}</p>}

      {solved !== null && (
        <div className="mt-3">
          <TrackProgressBar solved={solved} total={track.tasks_total} />
        </div>
      )}
    </article>
  )
}
